import React from "react";
import {
    ContentSection,
    GridRow,
    GridColumn,
    LabeledText,
    LabeledLink,
    ButtonRenderProps,
    LabeledDate,
    LabeledDateTime,
    LabeledBadge,
    BadgeColor
} from "sprint-common-web";

import {
    DeliveryInfoButtonAsideProps,
    DeliveryInfoButtonAside
} from "../../../../../../common/components/delivery-info-button-aside/DeliveryInfoButtonAside";
import { LabeledRecordTypeBadge } from "../../../../../../common/components/RecordTypeBadge";
import { LabeledTrailerInfoBadge } from "../../../../../../common/components/trailer-info/LabeledTrailerInfoBadge";
import { getFileRecordDetailsHref } from "../../../../../../content-ingestion/ContentIngestionRoutes";
import { SprintAccessRight } from "../../../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../../../core/security/useAccessVerifier";
import { useCloudAssetDetails } from "../../../common/hooks/useCloudAssetDetails";
import { AssetStatusButtonProps, CloudAssetStatusButton } from "./CloudAssetStatusButton";

export const CloudLibrarySection = (): JSX.Element => {
    const { isAuthorized } = useAccessVerifier();

    const { data: assetDetails, isLoading } = useCloudAssetDetails();

    const buttons: ButtonRenderProps[] = [];

    if (assetDetails) {
        const deliveryInfoButton: ButtonRenderProps<DeliveryInfoButtonAsideProps> = {
            itemId: assetDetails.deliveryId,
            isLabeled: true,
            render: DeliveryInfoButtonAside
        };

        buttons.push(deliveryInfoButton);
    }

    const assetStatusButton: ButtonRenderProps<AssetStatusButtonProps> = {
        render: CloudAssetStatusButton,
        disabled: !isAuthorized([SprintAccessRight.AssetsReadWrite])
    };

    buttons.push(assetStatusButton);

    const InfoView = assetDetails?.isTrailer && <LabeledTrailerInfoBadge />;

    return (
        <ContentSection header="Library" buttons={buttons} isLoading={isLoading} style={{ height: 450 }}>
            <GridRow>
                <GridColumn sm={6}>
                    <LabeledBadge
                        label="Asset type"
                        value="Cloud"
                        testId="record-type-field"
                        color={BadgeColor.LightGreen}
                        badgeSize={14}
                    />
                    <LabeledRecordTypeBadge value={assetDetails?.type} />
                    {InfoView}
                    <LabeledText
                        label="File path"
                        value={assetDetails?.filePath}
                        maxLength={40}
                        testId="file-path-field"
                        textTooltip
                    />
                    <LabeledText
                        label="Delivery file name"
                        value={assetDetails?.deliveryFileName}
                        maxLength={40}
                        testId="delivery-file-name-field"
                        textTooltip
                    />
                    {assetDetails?.sourceFileName && (
                        <LabeledText
                            label="Source file name"
                            value={assetDetails?.sourceFileName}
                            maxLength={40}
                            testId="source-file-name-field"
                            textTooltip
                        />
                    )}
                </GridColumn>
                <GridColumn sm={6}>
                    {assetDetails?.fileRecordId && (
                        <LabeledLink
                            label="File record"
                            value="Go to file record details"
                            maxLength={40}
                            testId="file-record-link"
                            href={getFileRecordDetailsHref(assetDetails.fileRecordId)}
                            underlineHref={true}
                        />
                    )}
                    <LabeledText label="Originator" value={assetDetails?.originator} testId="originator-field" />
                    <LabeledDate label="Arrival date" value={assetDetails?.arrivalDate} testId="arrival-date" />
                    <LabeledDateTime label="Ingested date" value={assetDetails?.ingestedDate} testId="ingested-date" />
                </GridColumn>
            </GridRow>
        </ContentSection>
    );
};
